import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Button, Chip, Card, CardBody } from '@heroui/react';
import { CheckCircle2, Play, ArrowRight, BookOpen, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CourseGuide, GuideProgressItem } from '@/services/apis/courses';
import { useTranslation } from '@/hooks/useTranslation';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/utils/cn';

interface CourseGuideCardProps {
  courseGuide: CourseGuide;
  index: number;
  guideProgressMap: Map<string, GuideProgressItem> | null | undefined;
  isEnrolled: boolean;
  previousGuideId?: string | null;
  language?: string;
}

const CourseGuideCard: React.FC<CourseGuideCardProps> = ({
  courseGuide,
  index,
  guideProgressMap,
  isEnrolled,
  previousGuideId,
  language = 'en',
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('courses');
  const { currentLanguage } = useLanguage();

  const displayLanguage = language === 'all' ? currentLanguage : language;
  const guide = courseGuide.guide;
  const guideTranslation = guide.translations?.find((t) => t.language === displayLanguage) || guide.translations?.[0];
  const guideTitle = guideTranslation?.title || 'Untitled Guide';
  const guideDescription = guideTranslation?.description || '';

  const stepCount = guide.steps?.length || 0;
  const estimatedTime = guide.estimatedTime ? `${guide.estimatedTime} min` : null;

  // Determine guide status from guide progress map
  const guideProgress = guideProgressMap?.get(guide.id);
  const isCompleted = useMemo(() => {
    if (!isEnrolled) return false;
    return guideProgress?.isCompleted || false;
  }, [guideProgress, isEnrolled]);

  const hasStarted = useMemo(() => {
    if (!isEnrolled) return false;
    return guideProgress?.hasStarted || false;
  }, [guideProgress, isEnrolled]);

  // Check if previous guide is completed (for enabling next guide)
  const isPreviousCompleted = useMemo(() => {
    if (index === 0) return true; // First guide is always enabled if enrolled
    if (!isEnrolled || !previousGuideId) return false;
    const previousProgress = guideProgressMap?.get(previousGuideId);
    return previousProgress?.isCompleted || false;
  }, [index, guideProgressMap, isEnrolled, previousGuideId]);

  // Determine if guide is enabled
  const isEnabled = isEnrolled && (index === 0 || isPreviousCompleted || isCompleted);

  const handleStartGuide = () => {
    if (isEnabled) {
      navigate(`/guides/${guide.id}`);
    }
  };

  const getButtonText = () => {
    if (isCompleted) return t('guideCard.completed');
    if (hasStarted) return t('guideCard.continue');
    return t('guideCard.start');
  };

  const getButtonIcon = () => {
    if (isCompleted) return <CheckCircle2 className="w-4 h-4" />;
    return <Play className="w-4 h-4" />;
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      <Card
        className={cn(
          'bg-white dark:bg-gray-900 border transition-all duration-300',
          isEnabled
            ? 'border-gray-200/60 dark:border-gray-800/60 shadow-sm hover:shadow-lg'
            : 'border-gray-100 dark:border-gray-800/30 opacity-60',
          isCompleted && 'border-green-200 dark:border-green-800/50 bg-green-50/30 dark:bg-green-950/10',
        )}
      >
        <CardBody className="p-6">
          <div className="flex items-start gap-4">
            {/* Step Number Badge */}
            <div
              className={cn(
                'flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg shadow-md transition-all',
                isCompleted
                  ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white'
                  : hasStarted && isEnabled
                  ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white'
                  : isEnabled
                  ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
              )}
            >
              {isCompleted ? <CheckCircle2 className="w-6 h-6" /> : index + 1}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">{guideTitle}</h3>
                  {guideDescription && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">{guideDescription}</p>
                  )}

                  {/* Stats */}
                  <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                    {estimatedTime && (
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-4 h-4" />
                        <span>{estimatedTime}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5">
                      <BookOpen className="w-4 h-4" />
                      <span>
                        {stepCount} {stepCount === 1 ? t('guideCard.step') : t('guideCard.steps')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Status Badge */}
                {isCompleted && (
                  <Chip
                    size="sm"
                    color="success"
                    variant="flat"
                    startContent={<CheckCircle2 className="w-3 h-3" />}
                    className="flex-shrink-0"
                  >
                    {t('guideCard.completed')}
                  </Chip>
                )}
                {hasStarted && !isCompleted && (
                  <Chip
                    size="sm"
                    color="primary"
                    variant="flat"
                    startContent={<Play className="w-3 h-3" />}
                    className="flex-shrink-0"
                  >
                    {t('guideCard.inProgress')}
                  </Chip>
                )}
              </div>

              {/* Action Button */}
              <div className="mt-4">
                <Button
                  color={isCompleted ? 'success' : 'primary'}
                  variant={isEnabled ? 'solid' : 'flat'}
                  onPress={handleStartGuide}
                  isDisabled={!isEnabled}
                  className={cn(
                    'font-semibold',
                    isEnabled &&
                      !isCompleted &&
                      'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 text-white border-0',
                  )}
                  startContent={getButtonIcon()}
                  endContent={isEnabled && !isCompleted ? <ArrowRight className="w-4 h-4" /> : undefined}
                >
                  {getButtonText()}
                </Button>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    </motion.div>
  );
};

export default CourseGuideCard;
